import type { SalesChecklist } from "@expert/contentful";

import { Accordion, Divider, Flex, ScrollArea, Text } from "@mantine/core";
import { type JSX, useCallback, useMemo, useState } from "react";

import { useSalesContext } from "../../../../sales-context/SalesContext";
import { useOfferChecklistStore } from "../../../../stores/useOfferChecklistStore";
import { DynamicCoreBenefits } from "../../../dynamic-smart-offer/DynamicCoreBenefits/DynamicCoreBenefits";
import { DynamicToneGuidance } from "../../../dynamic-smart-offer/DynamicToneGuidance/DynamicToneGuidance";
import { useDynamicSmartOffer } from "../../../dynamic-smart-offer/hooks/useDynamicSmartOffer";
import { ChecklistIcon } from "../Icons";
import { LanguageSelector } from "../language-selector/LanguageSelector";
import { ChecklistSectionWithErrorBoundary } from "./components/ChecklistSectionWithErrorBoundary";
import { isSectionComplete } from "./utils/isSectionComplete";

export type FullOfferChecklistProps = {
    salesChecklist: SalesChecklist;
};

export function FullOfferChecklist({ salesChecklist }: FullOfferChecklistProps): JSX.Element | null {
    const { sessionId, partner, logger, analytics, features } = useSalesContext();
    const {
        data: dynamicSmartOfferData,
        isPending,
        isAwaitingData,
        isFallback,
        isEnabled,
        showLatest,
        hasNewerData,
    } = useDynamicSmartOffer({
        sessionId,
        salesChecklist,
        partner,
        dispatcher: analytics.dispatcher,
        logger,
        isDynamicSmartOfferEnabled: features.isDynamicSmartOfferEnabled,
    });

    const firstSectionToOpen = useMemo(
        () =>
            salesChecklist.checklistSections.find(
                (section) =>
                    section.checklistItems.length !== 0 &&
                    !isSectionComplete(section.checklistItems, useOfferChecklistStore.getState().getData(sessionId)),
            )?.title,
        [salesChecklist, sessionId],
    );

    const [accordionValue, setAccordionValue] = useState<string[] | undefined>(
        firstSectionToOpen ? [firstSectionToOpen] : undefined,
    );

    const onSectionComplete = useCallback(
        (title: string) => {
            setAccordionValue((prevState) => {
                const indexOfCompletedSection = salesChecklist.checklistSections.findIndex(
                    (section) => section.title === title,
                );
                const nextTitle = salesChecklist.checklistSections.at(indexOfCompletedSection + 1)?.title;
                if (nextTitle) {
                    prevState?.push(nextTitle);
                }

                if (prevState?.includes(title)) {
                    prevState = prevState.filter((t) => t !== title);
                }
                return prevState;
            });
        },
        [salesChecklist.checklistSections],
    );

    return (
        <Flex data-testid="step-two-content" direction="column" w="100%">
            <ScrollArea h="calc(100% - var(--customer-panel-bottom-control-footer-height))">
                <Flex id="full-offer-checklist-header" c="dark.2" p={"sm"} pb="xxs" justify="space-between">
                    <Flex align="center">
                        <ChecklistIcon />
                        <Text fw={700} fz="xs" pl="3xs">
                            OFFER CHECKLIST
                        </Text>
                    </Flex>
                    <LanguageSelector />
                </Flex>

                <Divider size="xs" color="dark.4" />

                <DynamicToneGuidance
                    isEnabled={isEnabled}
                    isPending={isPending}
                    isAwaitingData={isAwaitingData}
                    hasNewerData={hasNewerData}
                    isFallback={isFallback}
                    toneGuidance={dynamicSmartOfferData.toneGuidance}
                    onShowLatest={showLatest}
                />

                <Accordion
                    transitionDuration={250}
                    multiple
                    value={accordionValue}
                    onChange={(section) => setAccordionValue(section)}
                >
                    {salesChecklist.checklistSections.map((section) => {
                        if (section.checklistItems.length === 0) return null;
                        // RDESQ-1988: when flag is ON, Discovery/Engagement sections are pushed
                        // to the Expert Assist timeline instead of rendering in the checklist.
                        if (
                            features.isUsingMovedDiscoveryAndEngagementQuestions &&
                            (section.sectionType === "discovery" || section.sectionType === "engagement")
                        ) {
                            return null;
                        }

                        return (
                            <ChecklistSectionWithErrorBoundary
                                key={section.id}
                                section={section}
                                onSectionComplete={onSectionComplete}
                                prependSlot={
                                    isDynamicCoreBenefitsSection(section) ? (
                                        <DynamicCoreBenefits
                                            isEnabled={isEnabled}
                                            isPending={isPending}
                                            isAwaitingData={isAwaitingData}
                                            pitchPoints={dynamicSmartOfferData.pitchPoints}
                                        />
                                    ) : undefined
                                }
                                hiddenItemKeys={
                                    isDynamicCoreBenefitsSection(section) && isEnabled ? ["COVERAGE"] : undefined
                                }
                            />
                        );
                    })}
                </Accordion>
            </ScrollArea>
        </Flex>
    );
}

function isDynamicCoreBenefitsSection(section: { sectionType?: string; enableDynamicContent?: boolean }) {
    return section.sectionType === "coreBenefits" && section.enableDynamicContent;
}
